import React from 'react';

import { Currency } from 'components/common/DataFormat';
import Wahanda from 'common/wahanda';
import type { SharedProps } from 'reconnect-types/moduleFederation';
import { dataCell, rightAlignedHeader, stackedGroupHeader } from '../common/reportTableCells';

type ServiceSalesMetrics = {
  numberIncludingCancelled?: number;
  grossTotalIncludingCancelled?: number;
  cancelledNumber?: number;
  cancelledGrossTotal?: number;
  number?: number;
  grossTotalLeftover?: number;
  paidBySalonVoucher?: number;
};

type ServiceSalesRow = ServiceSalesMetrics & {
  service?: string;
  variantRows?: ServiceSalesRow[];
};

type ServiceSalesTypeRow = ServiceSalesMetrics & {
  type?: string;
  rows?: ServiceSalesRow[];
};

export type ServiceSalesReportTableProps = {
  rows: ServiceSalesTypeRow[];
  total: ServiceSalesMetrics;
};

const buildDataCells = (row: ServiceSalesTypeRow | ServiceSalesRow) => [
  dataCell(row.numberIncludingCancelled ?? 0),
  dataCell(<Currency amount={row.grossTotalIncludingCancelled} />),
  dataCell(row.cancelledNumber ?? 0),
  dataCell(<Currency amount={row.cancelledGrossTotal} alwaysNegative />),
  dataCell(<Currency amount={row.paidBySalonVoucher ?? 0} />),
  dataCell(row.number ?? 0),
  dataCell(<Currency amount={row.grossTotalLeftover ?? 0} />, { variant: 'grandTotal' }),
];

export const getServiceSalesReportTableProps = ({
  rows,
  total,
}: ServiceSalesReportTableProps): SharedProps['ReportTable'] => {
  const headings = Wahanda.lang.reports.sales.serviceSalesReport.headings;

  const headerRows = [
    [
      { content: '\u00a0', width: 'wide' as const },
      stackedGroupHeader(headings.salesIncludingCancelled),
      stackedGroupHeader(headings.salesCancelled),
      {
        content: headings.prepaid,
        align: 'right' as const,
        width: 'narrow' as const,
        height: 'double' as const,
      },
      stackedGroupHeader(headings.salesTotal),
    ],
    [
      { content: '\u00a0' },
      ...Array.from({ length: 2 }, () => [
        rightAlignedHeader(headings.quantity),
        rightAlignedHeader(headings.amountIncludingVat),
      ]).flat(),
      { content: '', align: 'right' as const, width: 'narrow' as const },
      rightAlignedHeader(headings.quantity),
      rightAlignedHeader(headings.amountIncludingVat),
    ],
  ];

  const tableRows = rows.flatMap((serviceType) => [
    {
      id: serviceType.type,
      variant: 'rowHeading' as const,
      dataTest: 'service-sale-category',
      cells: [{ content: serviceType.type ?? '' }, ...buildDataCells(serviceType)],
    },
    ...(serviceType.rows ?? []).map((row) => {
      const serviceLabel = row.service?.trim() || 'service';
      const serviceId = `${serviceType.type ?? 'type'}-${serviceLabel}`;
      const variantRows = row.variantRows ?? [];

      return {
        id: serviceId,
        cells: [
          { content: row.service ?? '', variant: 'rowSubHeading' as const },
          ...buildDataCells(row),
        ],
        ...(variantRows.length > 0
          ? {
              variantRows: variantRows.map((variant) => {
                const variantLabel = variant.service?.trim() || 'variant';

                return {
                  id: `${serviceId}-variant-${variantLabel}`,
                  variant: 'variantRow' as const,
                  dataTestId: 'service-sale-variant',
                  cells: [{ content: variant.service ?? '' }, ...buildDataCells(variant)],
                };
              }),
            }
          : {}),
      };
    }),
  ]);

  const footerRow = {
    cells: [
      { content: headings.grandTotal, variant: 'rowHeading' as const },
      ...buildDataCells(total).map((cell) => ({ ...cell, variant: 'grandTotal' as const })),
    ],
  };

  return {
    headerRows,
    rows: tableRows,
    footerRow,
    testID: 'service-sales-report-table',
  };
};
